import { ChevronLeftIcon } from "@heroicons/react/24/solid";
import { format } from "date-fns";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button, Column, Dialog, Headline, Paragraph, Table } from "@fronterahealth/frontera-ui-components";

import {
  ApiTaskStatusChoices,
  ChargeSavedPaymentMethodMutation,
  ChatType,
  useChargeSavedPaymentMethodMutation,
  useGetBillingChatMembersQuery,
} from "@api/graphql/types-and-hooks";

interface BillingChat extends ChatType {
  id: string;
  taskCount: number;
  completedTaskCount: number;
  caregiverName: string;
  clientName: string;
  serviceSelected: string;
  paymentStatus?: string;
  paymentMethodId?: string;
  paymentIntentId?: string;
}

export const BillingPage: React.FC = () => {
  const navigate = useNavigate();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedChat, setSelectedChat] = useState<BillingChat | null>(null);
  const [chargingChatId, setChargingChatId] = useState<string | null>(null);

  const pageItemsDefault = 10;

  // Payment mutation
  const chargeSavedPaymentMethodMutation = useChargeSavedPaymentMethodMutation();
  const { mutate: chargeSavedPaymentMethod } = chargeSavedPaymentMethodMutation;

  // Provider's billable expert chats come from their ChatMember rows (PAR-2194),
  // not the shared chats resolver. chatMembers has no chat-field filters, so the
  // Table paginates + searches the full (bounded) caseload client-side.
  const { data, isFetching, refetch } = useGetBillingChatMembersQuery({}, { queryKey: ["getBillingChatMembers"] });

  const chats = (data?.chatMembers?.edges?.map((edge) => edge?.node?.chat).filter(Boolean) ?? []) as ChatType[];

  // Transform chat data for billing display
  const billingChats: BillingChat[] = chats.map((chat: ChatType) => {
    const caregiverName = chat.learner?.relationshipSet?.edges?.[0]?.node?.caregiver?.caregivermetadata
      ? `${chat.learner.relationshipSet.edges[0].node.caregiver.caregivermetadata.firstName} ${chat.learner.relationshipSet.edges[0].node.caregiver.caregivermetadata.lastName}`
      : "N/A";

    const clientName = chat.learner?.learnermetadata
      ? `${chat.learner.learnermetadata.firstName} ${chat.learner.learnermetadata.lastName}`
      : "N/A";

    // Count tasks from chatTasks
    const taskCount = chat.chatTasks?.edges?.length || 0;

    // Count completed tasks
    const completedTaskCount =
      chat.chatTasks?.edges?.filter((task) => task?.node?.status === ApiTaskStatusChoices.Success).length || 0;

    // Get payment information
    const billingDetails = chat.user?.userBillingDetails?.edges?.[0]?.node;
    const paymentStatus = billingDetails?.paymentStatus ?? undefined;
    const paymentMethodId = billingDetails?.paymentMethodId ?? undefined;
    const paymentIntentId = billingDetails?.paymentIntentId ?? undefined;

    // For now, we'll use a placeholder for service selected
    // This would need to be added to the GraphQL schema if not already available
    const serviceSelected = billingDetails?.description || "N/A"; // This should come from the actual service data

    return {
      ...chat,
      id: chat.id,
      taskCount,
      completedTaskCount,
      caregiverName,
      clientName,
      serviceSelected,
      paymentStatus,
      paymentMethodId,
      paymentIntentId,
    };
  });

  const handleCompletePaymentClick = (chat: BillingChat) => {
    setSelectedChat(chat);
    setConfirmDialogOpen(true);
  };

  const handleConfirmPayment = () => {
    if (!selectedChat?.paymentIntentId) {
      console.error("No payment intent ID found for chat:", selectedChat?.id);
      return;
    }

    setChargingChatId(selectedChat.id);
    setConfirmDialogOpen(false);

    chargeSavedPaymentMethod(
      {
        paymentIntentId: selectedChat.paymentIntentId,
      },
      {
        onSuccess: (result: ChargeSavedPaymentMethodMutation) => {
          if (result.chargeSavedPaymentMethod?.status) {
            // Refresh the billing data to show updated payment status
            refetch();
            // TODO: Show success notification
          } else {
            console.error("Payment failed:", result.chargeSavedPaymentMethod?.message);
            // TODO: Show error notification
          }
          setChargingChatId(null);
        },
        onError: (error: unknown) => {
          console.error("Error processing payment:", error);
          // TODO: Show error notification
          setChargingChatId(null);
        },
      },
    );
  };

  const handleCancelPayment = () => {
    setConfirmDialogOpen(false);
    setSelectedChat(null);
  };

  const columns: Column<BillingChat>[] = [
    {
      dataIndex: "createdDate",
      key: "createdDate",
      title: "Chat Created",
      render: (value: string) => format(new Date(value), "MMM dd, yyyy HH:mm"),
    },
    {
      dataIndex: "caregiverName",
      key: "caregiverName",
      title: "Caregiver Name",
      render: (value: string) => <span className="ph-no-capture">{value || "-"}</span>,
    },
    {
      dataIndex: "clientName",
      key: "clientName",
      title: "Client Name",
      render: (value: string) => <span className="ph-no-capture">{value || "-"}</span>,
    },
    {
      dataIndex: "type",
      key: "type",
      title: "Chat Type",
      render: (value: string) => value || "N/A",
    },
    {
      dataIndex: "status",
      key: "status",
      title: "Chat Status",
      render: (value: string) => <span className="text-sm capitalize">{value || "N/A"}</span>,
    },
    {
      dataIndex: "summaryStatus",
      key: "summaryStatus",
      title: "Summary Status",
      render: (value: string) => <span className="text-sm capitalize">{value || "N/A"}</span>,
    },
    {
      dataIndex: "clinicianSummaryStatus",
      key: "clinicianSummaryStatus",
      title: "Clinician Summary Status",
      render: (value: string) => <span className="text-sm capitalize">{value || "N/A"}</span>,
    },
    {
      key: "taskDistribution",
      dataIndex: "id",
      title: "Tasks (Completed/Total)",
      render: (_, record: BillingChat) => (
        <div className="text-sm">
          <span className="font-medium text-green-600">{record.completedTaskCount}</span>
          <span className="text-gray-400"> / </span>
          <span className="text-gray-600">{record.taskCount}</span>
        </div>
      ),
    },

    {
      dataIndex: "serviceSelected",
      key: "serviceSelected",
      title: "Service Selected",
    },
    {
      key: "actions",
      dataIndex: "id",
      title: "Actions",
      className: "w-48",
      render: (_, record: BillingChat) => {
        const { paymentStatus, paymentMethodId, paymentIntentId } = record;
        const isThisRowCharging = chargingChatId === record.id;

        if (paymentStatus === "PENDING" && paymentMethodId && paymentIntentId) {
          return (
            <Button
              text={isThisRowCharging ? "Processing..." : "Complete Payment"}
              appearance="primary"
              onClick={() => handleCompletePaymentClick(record)}
              disabled={isThisRowCharging}
            />
          );
        }

        return <span className="text-sm text-gray-600 capitalize">{paymentStatus || "No payment info"}</span>;
      },
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-row gap-2 items-center cursor-pointer" onClick={() => navigate("/frontera-clarity")}>
        <ChevronLeftIcon className="text-green-700 w-5 h-5" />
        <Paragraph size="small">Back</Paragraph>
      </div>

      <div className="space-y-4">
        <div>
          <Headline size="medium" className="text-2xl font-semibold text-gray-900">
            Billing
          </Headline>
          <Paragraph colorType="secondary" className="mt-2">
            Manage billing for your chat consultations and services
          </Paragraph>
        </div>

        <div className="flow-root bg-white w-full">
          <Table
            defaultItemsPerPage={pageItemsDefault}
            showPagination
            loading={isFetching}
            showSearch
            inputClassName="w-96"
            itemsPerPageOptions={[5, 10, 20, 50]}
            actions={[]}
            data={billingChats}
            columns={columns}
          />
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog
        title="Confirm Payment"
        open={confirmDialogOpen}
        setOpen={setConfirmDialogOpen}
        primaryButton={{
          text: "Confirm Payment",
          onClick: handleConfirmPayment,
        }}
        secondaryButton={{
          text: "Cancel",
          onClick: handleCancelPayment,
        }}
        size="sm"
      >
        <div className="space-y-4">
          <Paragraph>Are you sure you want to charge the payment for this {selectedChat?.serviceSelected}?</Paragraph>
          {selectedChat && (
            <div className="bg-gray-50 p-3 rounded-md">
              <Paragraph size="small" className="font-medium ph-no-capture">
                Client: {selectedChat.clientName}
              </Paragraph>
              <Paragraph size="small" className="text-gray-600 ph-no-capture">
                Caregiver: {selectedChat.caregiverName}
              </Paragraph>
              <Paragraph size="small" className="text-gray-600">
                Chat Date: {format(new Date(selectedChat.createdDate), "MMM dd, yyyy HH:mm")}
              </Paragraph>
            </div>
          )}
        </div>
      </Dialog>
    </div>
  );
};
